<template>
  <NuxtRouteAnnouncer />
  <ClientOnly>
    <LazyIdleToaster v-bind="{ toasts }" hydrate-on-idle @close="removeToast" />
  </ClientOnly>
  <NuxtLayout>
    <NuxtPwaManifest />
    <LegacyWarning />
    <AcknowledgementModal />

    <NuxtPage v-if="!isBlockedForAndroid" />

    <LazyModal
      v-else
      :is-visible="isBlockedForAndroid"
      :tracking="{
        topicKey: 'location-blocked-for-android',
      }"
      @cancel="onAndroidDisclaimerCancel"
      @confirm="onAndroidDisclaimerConfirm"
    >
      <template #headline>
        {{ $t('restriction.android.title') }}
      </template>
      <p>
        {{ $t('restriction.android.text') }}
      </p>
      <template #confirm>
        {{ $t('restriction.android.cta.primary') }}
      </template>
      <template #cancel>
        {{ $t('restriction.android.cta.secondary') }}
      </template>
    </LazyModal>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import '@lottocom/frontend-lotteries/style'

import type { SupportedLocales } from '#i18n/types'

import { DrawChangedCause } from '#api/shared/@types/generated/subscription/graphql-schema-types'
import { useAcknowledgement } from '~/composables/acknowledgement'
import broadcasterInstance, { BroadcastMessage } from '~/utils/broadcaster'

const isNativeApp = useState(StateKey.IsNativeApp)
const { accessToken, isLoggedIn } = useAuth()
const { isBlockedForAndroid } = useCustomContext()
const licensedTerritory = useLicensedTerritory({ raw: true })
const { defaultLocale, locale, setLocale, t } = useI18n()
const { addToast, removeToast, toasts } = useToaster()
const switchLocalePath = useSwitchLocalePath()
const { currentRoute } = useRouter()
const { getSafeLocale, isLocaleSupported } = useLocaleUtils()
const {
  clearDefaultTeasersCache,
  clearDisclaimerAcceptedCache,
  clearHighFrequencyOrdersCache,
  clearPlayerCache,
  clearResultsCache,
  clearScratchcardOrdersCache,
  clearSingleOrdersCache,
  clearSubscriptionOrdersCache,
  clearTeasersCache,
  clearTransactionsCache,
  clearHighFrequencyResultsAndWinnersCache,
} = useApolloCache()
const { checkAcknowledgementStatus } = useAcknowledgement()

useHead({
  htmlAttrs: {
    lang: computed(() => locale.value),
  },
})

// Setup broadcaster listeners only on client side
const setupBroadcaster = () => {
  broadcasterInstance.on(BroadcastMessage.ClearPlayerCache, clearPlayerCache)

  broadcasterInstance.on(BroadcastMessage.ClearDefaultTeasersCache, clearDefaultTeasersCache)

  broadcasterInstance.on(
    BroadcastMessage.ClearDisclaimerAcceptedCache,
    clearDisclaimerAcceptedCache,
  )

  broadcasterInstance.on(BroadcastMessage.ClearResultsCache, clearResultsCache)

  broadcasterInstance.on(
    BroadcastMessage.ClearHighFrequencyOrdersCache,
    clearHighFrequencyOrdersCache,
  )

  broadcasterInstance.on(BroadcastMessage.ClearScratchcardOrdersCache, clearScratchcardOrdersCache)

  broadcasterInstance.on(BroadcastMessage.ClearSingleOrdersCache, clearSingleOrdersCache)

  broadcasterInstance.on(
    BroadcastMessage.ClearSubscriptionOrdersCache,
    clearSubscriptionOrdersCache,
  )

  broadcasterInstance.on(BroadcastMessage.ClearTeasersCache, clearTeasersCache)

  broadcasterInstance.on(BroadcastMessage.ClearTransactionsCache, clearTransactionsCache)

  broadcasterInstance.on(BroadcastMessage.ContextChange, () => {
    window.location.reload()
  })

  broadcasterInstance.on(
    BroadcastMessage.clearHighFrequencyResultsAndWinnersCache,
    clearHighFrequencyResultsAndWinnersCache,
  )
}

// Setup subscriptions only on client side
const setupSubscriptions = () => {
  const drawChangesSubscription = useDrawChangesSubscription({
    licensedTerritory,
  })
  drawChangesSubscription.onResult(({ data }) => {
    clearDefaultTeasersCache()
    clearTeasersCache()

    switch (data?.drawChanges.cause) {
      case DrawChangedCause.Evaluation:
        clearResultsCache()
        clearSingleOrdersCache()
        clearSubscriptionOrdersCache()
        break
    }
  })

  const playSlipChangesSubscription = usePlaySlipChangesSubscription(
    { accessToken },
    { enabled: !!accessToken.value },
  )
  playSlipChangesSubscription.onResult(({ data }) => {
    if (data?.playSlipChanges.subscription) {
      clearSubscriptionOrdersCache()
      return
    }

    clearSingleOrdersCache()
  })

  const scratchcardOrdersReadySubscription = useScratchcardOrdersReadySubscription(
    { accessToken },
    { enabled: !!accessToken.value },
  )
  scratchcardOrdersReadySubscription.onResult(() => clearScratchcardOrdersCache())

  const highFrequencyResultsAndWinnersSubscription =
    useHighFrequencyResultsAndWinnersUpdatesSubscription({
      licensedTerritory,
    })

  highFrequencyResultsAndWinnersSubscription.onResult(() => {
    if (String(currentRoute.value.name).includes('home-keno')) return
    clearHighFrequencyResultsAndWinnersCache()
  })
}

const visitUrl = (url: string) => {
  if (import.meta.server) return
  try {
    window.location.href = new URL(url).toString()
  } catch {
    // Do nothing
  }
}

const onAndroidDisclaimerCancel = () => {
  visitUrl(t('restriction.android.url.secondary'))
}

const onAndroidDisclaimerConfirm = () => {
  visitUrl(`${t('restriction.android.url.primary')}/?leaveApp=true`)
}

onBeforeMount(() => {
  Object.defineProperty(window, 'isAppWrapper', {
    configurable: true,
    get: () => isNativeApp.value,
    set: (newValue) => (isNativeApp.value = newValue === true),
  })
})

const logUserOnlineStatus = () => {
  const { log } = useDatadog()
  const online = useOnline()
  const userWasOffline = ref(false)

  watch(
    online,
    () => {
      if (online.value && userWasOffline.value) {
        log(DATADOG_LOG_TYPES.Info, {
          appendPlayerInfo: true,
          message: 'User is online again',
          messageContext: {
            url: window.location.href,
          },
        })
      }
      if (!online.value) {
        log(DATADOG_LOG_TYPES.Info, {
          appendPlayerInfo: true,
          message: 'User is offline',
          messageContext: {
            url: window.location.href,
          },
        })
        userWasOffline.value = true
      }
    },
    {
      immediate: true,
    },
  )
}

const LOCALE_SWITCH_TOAST_ID = 'locale_switch_suggested'

const { oldLocale } = useLanguageUtils()

const checkBrowserLocale = () => {
  let playerLocale = oldLocale.value

  if (!isLoggedIn.value) {
    const browserLocales = import.meta.client
      ? navigator.languages?.map((lang) => lang.split('-')[0])
      : undefined

    playerLocale = (browserLocales?.find((locale) => isLocaleSupported(locale)) ??
      defaultLocale) as SupportedLocales['code']
  }

  const translationLocale = getSafeLocale(playerLocale ?? defaultLocale)

  if (playerLocale === undefined) return
  if (locale.value === playerLocale) return
  if (playerLocale && !isLocaleSupported(playerLocale) && locale.value === defaultLocale) return

  addToast(
    {
      autoClose: false,
      headline: t('localeswitch.toast.headline', 0, {
        locale: translationLocale,
      }),
      message: {
        locale: translationLocale,
        translationKey: 'localeswitch.toast.message',
        translationVariables: {
          cta: {
            text: t('localeswitch.toast.cta', 0, {
              locale: translationLocale,
            }),
            to: () => switchLocalePath(translationLocale),
            type: 'link',
          },
        },
      },
      onLinkClicked: async () => {
        removeToast(LOCALE_SWITCH_TOAST_ID)
        await setLocale(translationLocale)
      },
      tracking: { topicKey: 'locale_switch_suggested' },
      type: 'info',
    },
    LOCALE_SWITCH_TOAST_ID,
  )
}

watchImmediate(isLoggedIn, () => {
  if (!isLoggedIn.value) return
  setTimeout(() => {
    checkAcknowledgementStatus()
  }, 1000)
})

onMounted(() => {
  checkBrowserLocale()

  // Initialize broadcaster listeners only on client
  setupBroadcaster()

  // Initialize subscriptions only on client
  setupSubscriptions()

  logUserOnlineStatus()
  useRokt()?.loadRokt()
})
</script>
